import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Eye } from "lucide-react";
import { getHotDeals } from "../../../api/productApi";
import { HotDealShimmer } from "../../../components/ShimmerEffect";

const HotDealSection = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const fetchDeals = async () => {
      try {
        const response = await getHotDeals();
        if (response.success && response.data && response.data.products?.length > 0) {
          setDeals(response.data.products);
        }
      } catch (error) {
        console.error("Error fetching hot deals:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDeals();
  }, []);

  // Auto-play carousel logic
  useEffect(() => {
    if (deals.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % deals.length);
    }, 8000); // Change slide every 8 seconds
    return () => clearInterval(interval);
  }, [deals.length]);

  if (loading) return <HotDealShimmer />;
  if (deals.length === 0) return null;

  const currentDeal = deals[currentIndex];
  
  // Calculate savings
  const originalPrice = currentDeal.mrp || (currentDeal.basePrice / (1 - (currentDeal.discountPercentage / 100)));
  const savings = Math.max(0, originalPrice - currentDeal.basePrice);
  const discountText = currentDeal.discountPercentage > 0 
    ? `${currentDeal.discountPercentage}% OFF`
    : (originalPrice > currentDeal.basePrice ? `${Math.round(((originalPrice - currentDeal.basePrice) / originalPrice) * 100)}% OFF` : null);

  return (
    <section className="py-8 md:py-10 bg-[#FDFBF7] dark:bg-gray-900 relative overflow-hidden transition-all duration-700">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header Section */}
        <div className="mb-8 md:mb-10 max-w-2xl animate-in slide-in-from-bottom-4 duration-700 fade-in relative z-10">
          <div className="flex items-center gap-4 mb-3">
            <div className="h-[1px] w-8 bg-amber-600"></div>
            <span className="text-[10px] md:text-xs font-bold uppercase tracking-[0.2em] text-amber-600">
              Featured Product of the Day
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-slate-900 dark:text-gray-100 tracking-tight leading-none mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            Today's Spotlight
          </h2>
          <p className="text-slate-500 dark:text-gray-400 text-sm md:text-base leading-relaxed">
            Handpicked daily — the deepest discount and biggest savings on a flagship pick.
          </p>
        </div>

        {/* Carousel Content */}
        <div className="relative min-h-[400px]">
          {deals.map((deal, index) => {
            const isActive = index === currentIndex;
            const dealOriginalPrice = deal.mrp || (deal.basePrice / (1 - (deal.discountPercentage / 100)));
            const dealSavings = Math.max(0, dealOriginalPrice - deal.basePrice);
            const dealDiscountText = deal.discountPercentage > 0 
              ? `${deal.discountPercentage}% OFF`
              : (dealOriginalPrice > deal.basePrice ? `${Math.round(((dealOriginalPrice - deal.basePrice) / dealOriginalPrice) * 100)}% OFF` : null);

            return (
              <div 
                key={deal._id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-center transition-opacity duration-700 ease-in-out absolute inset-0 w-full ${isActive ? 'opacity-100 z-10 pointer-events-auto' : 'opacity-0 z-0 pointer-events-none'}`}
              >
                {/* Image Section */}
                <div className="bg-[#F6F4EE] dark:bg-gray-800 rounded-[1.5rem] md:rounded-[2rem] p-6 md:p-8 relative flex items-center justify-center min-h-[250px] md:min-h-[400px]">
                  {/* Tags */}
                  <div className="absolute top-6 left-6 md:top-8 md:left-8 flex items-center gap-2 z-20">
                    <span className="flex items-center gap-1.5 text-[10px] md:text-xs font-bold uppercase tracking-wider text-red-600">
                      <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse"></span>
                      Live Deal
                    </span>
                  </div>
                  
                  {dealDiscountText && (
                    <div className="absolute top-6 right-6 md:top-8 md:right-8 w-16 h-16 md:w-20 md:h-20 bg-slate-900 dark:bg-blue-600 text-white rounded-full flex flex-col items-center justify-center shadow-2xl rotate-12 transform hover:scale-110 transition-transform cursor-default z-20">
                      <span className="text-sm md:text-lg font-black leading-none">{dealDiscountText.split(' ')[0]}</span>
                      <span className="text-[10px] md:text-xs font-bold mt-0.5">OFF</span>
                    </div>
                  )}

                  <img 
                    src={deal.images[0]} 
                    alt={deal.name}
                    className="w-full h-auto max-h-[280px] object-contain drop-shadow-2xl mix-blend-multiply dark:mix-blend-normal hover:scale-105 transition-transform duration-700 relative z-10"
                  />
                </div>

                {/* Details Section */}
                <div className="flex flex-col justify-center">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 block">
                    {deal.brand}
                  </span>
                  <h3 className="text-xl md:text-2xl font-black text-slate-900 dark:text-gray-100 leading-tight mb-4">
                    {deal.name}
                  </h3>

                  {/* Specs Tags */}
                  <div className="flex flex-wrap items-center gap-2 mb-8">
                    {deal.specifications?.processor && (
                      <span className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-300 text-[11px] font-bold uppercase rounded-lg shadow-sm">
                        {deal.specifications.processor.split(' ')[0]}
                      </span>
                    )}
                    {deal.specifications?.ram && (
                      <span className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-300 text-[11px] font-bold uppercase rounded-lg shadow-sm">
                        {deal.specifications.ram} RAM
                      </span>
                    )}
                    {deal.specifications?.storage && (
                      <span className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-300 text-[11px] font-bold uppercase rounded-lg shadow-sm">
                        {deal.specifications.storage} SSD
                      </span>
                    )}
                    {deal.condition && (
                      <span className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-300 text-[11px] font-bold uppercase rounded-lg shadow-sm">
                        {deal.condition}
                      </span>
                    )}
                  </div>

                  {/* Pricing */}
                  <div className="flex items-end gap-3 mb-1">
                    <div className="text-3xl md:text-4xl font-black text-slate-900 dark:text-gray-100 tracking-tight" style={{ fontFamily: 'Georgia, serif' }}>
                      ₹{deal.basePrice?.toLocaleString()}
                    </div>
                    {dealOriginalPrice > deal.basePrice && (
                      <div className="text-base text-slate-400 line-through font-medium mb-1">
                        ₹{dealOriginalPrice.toLocaleString()}
                      </div>
                    )}
                    {dealDiscountText && (
                      <div className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-bold rounded mb-2 ml-1">
                        {dealDiscountText}
                      </div>
                    )}
                  </div>
                  {dealSavings > 0 && (
                    <div className="text-xs font-bold text-emerald-600 mb-6">
                      You save ₹{dealSavings.toLocaleString()}
                    </div>
                  )}

                  {/* Features */}
                  <div className="flex flex-wrap items-center gap-2 mb-6">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F6F4EE] dark:bg-gray-800 rounded-full text-[11px] font-bold text-slate-600 dark:text-gray-300">
                      <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                      {deal.defaultWarranty || '12-Month'} Warranty
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F6F4EE] dark:bg-gray-800 rounded-full text-[11px] font-bold text-slate-600 dark:text-gray-300">
                      <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                      Free Shipping
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F6F4EE] dark:bg-gray-800 rounded-full text-[11px] font-bold text-slate-600 dark:text-gray-300">
                      <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                      COD Available
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-3">
                    <Link 
                      to={`/product/${deal._id}`}
                      className="px-6 py-3 bg-slate-900 dark:bg-blue-600 text-white rounded-full text-xs font-black hover:bg-slate-800 dark:hover:bg-blue-700 transition-colors flex items-center justify-center shadow-xl shadow-slate-900/20"
                    >
                      <ShoppingCart size={16} className="mr-2" /> ADD TO CART
                    </Link>
                    <Link 
                      to={`/product/${deal._id}`}
                      className="px-6 py-3 bg-white dark:bg-gray-800 text-slate-900 dark:text-gray-100 border border-slate-200 dark:border-gray-700 rounded-full text-xs font-bold hover:border-slate-400 dark:hover:border-gray-500 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-center"
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Carousel Indicators (Only show if multiple deals) */}
        {deals.length > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8 relative z-20">
            {deals.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                  currentIndex === idx ? 'bg-amber-600 w-8' : 'bg-slate-300 hover:bg-slate-400'
                }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default HotDealSection;
